import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { AbstractControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ClientDataDTO } from 'app/header-footer-template/model/ClientDataDTO';
import { ClientService } from 'app/services/client-service';
import { ToastrService } from 'ngx-toastr';
import { CommonsUtils } from 'app/commons/CommonsUtils';
import { AuthService } from 'app/security/auth.service';
import { CommonsText } from 'app/commons/CommonsText';
import { CustomDateValidator } from 'app/commons/custom-date-validator';

@Component({
    selector: 'app-profile-contact-edition',
    templateUrl: './profile-contact-edition.component.html',
    styleUrls: ['./profile-contact-edition.component.scss']
})
export class ProfileContactEditionComponent implements OnInit {

    @Output() profileAction = new EventEmitter<any>();

    contactForm: FormGroup;

    documentType: string;
    documentNumber: string;
    actualPostalCode: string = '00000';

    messages: string[] = null;
    running: boolean = false;
    loading: boolean = false;
    loadingCities: boolean = false;
    patching: boolean = false;

    contactProgress: number = 0;
    addressProgress: number = 0;
    contactFactor: any;
    addressFactor: any;

    roadTypeList: any[] = [];
    roadTypeSettings: any;
    province: string;
    citySettings: any;
    cityList: any[] = [];

    prefixData: any[];
    prefixSettings: any;

    isCompany: boolean = false;
    maxDate: Date;

    private readonly CODE_ERROR_EMAIL_ALREADY_REGISTERED = 'email.already_registered';
    private readonly ERROREMAILACONT = 'ERROREMAILACONT';
    private readonly ERRORMOBILECONT = 'ERRORMOBILECONT';
    private readonly ERRORPHONECONT = 'ERRORPHONECONT';
    private prefijoPhoneFlag: any = null;
    private prefijoFijoPhoneFlag: any = null;

    /**
     * Teléfono y prefijo van siempre juntos: el backend los concatena como "+<prefijo> <número>"
     * (ModifyClientCoreCmd), así que un número sin prefijo se guardaría como "+null 666999888" y
     * un prefijo suelto como "+34 null".
     */
    private readonly PHONE_PAIRS = [
        { phone: 'phone', prefix: 'phonePrefix' },
        { phone: 'mobilePhone', prefix: 'mobilePhonePrefix' }
    ];

    constructor(
        private formBuilder: FormBuilder,
        private clientService: ClientService,
        private toastr: ToastrService,
        private authService: AuthService
    ) {
        this.prefixData = CommonsUtils.phonePrefix();
        this.prefixSettings = {
            singleSelection: true,
            text: 'Prefijo',
            enableCheckAll: false,
            enableSearchFilter: true,
            enableFilterSelectAll: false,
            searchPlaceholderText: 'Filtro',
            showCheckbox: true,
            classes: 'multi-select-phone',
            clearAll: false
        };
    }

    ngOnInit() {
        this.setMaxDate();

        if (this.authService.isLogged()) {
            const user = this.authService.getUser();
            this.isCompany = !!(user.documentType && user.documentType === 'CIF');
        }

        this.contactFactor = this.clientService.getContactFactor();
        this.addressFactor = this.clientService.getAddressFactor();

        this.roadTypeSettings = {
            singleSelection: true,
            text: 'Tipo de vía',
            enableCheckAll: false,
            enableSearchFilter: true,
            enableFilterSelectAll: false,
            searchPlaceholderText: 'Filtro',
            classes: 'multi-select',
            clearAll: false
        };

        this.citySettings = {
            singleSelection: true,
            text: 'Introduzca un código postal y seleccione una población',
            enableCheckAll: false,
            enableSearchFilter: true,
            enableFilterSelectAll: false,
            searchPlaceholderText: 'Filtro',
            classes: 'multi-select',
            clearAll: false
        };

        this.contactForm = this.formBuilder.group({
            name: ['', [Validators.required]],
            surname: ['', [Validators.required]],
            lastName: ['', []],
            birthDate: ['', this.isCompany ? [] : [Validators.required, Validators.minLength(10), CustomDateValidator.minAge, CustomDateValidator.maxAge]],
            phonePrefix: ['', []],
            phone: ['', [CommonsUtils.validPhone('phonePrefix')]],
            mobilePhonePrefix: ['', [Validators.required]],
            mobilePhone: ['', [Validators.required, CommonsUtils.validMobilePhone('mobilePhonePrefix')]],
            email: ['', [Validators.required, Validators.email, Validators.pattern(CommonsUtils.EMAIL_REGEX)]],
            province: ['', []],
            postalCode: ['', [Validators.required]],
            cityCb: ['', [Validators.required]],
            roadType: ['', [Validators.required]],
            address: ['', [Validators.required]],
            number: ['', [Validators.required]],
            portal: ['', []],
            stair: ['', []],
            floor: ['', []],
            door: ['', []],
            notificationChannel: ['', []]
        });

        this.contactForm.valueChanges.subscribe(() => {
            if (!this.patching) {
                this.checkContactPercent();
                this.checkAddressPercent();
            }
            this.messages = null;
        });

        this.loadData();
    }

    private loadData() {
        this.loading = true;
        Promise.all([
            this.clientService.getClient(),
            this.clientService.getRoadTypes()
        ]).then(([client, roadTypesResponse]) => {
            this.loading = false;
            this.roadTypeList = roadTypesResponse.response;
            this.patchForm(client);
        }).catch(() => {
            this.loading = false;
            this.toastr.error(CommonsText.PROBLEMA_DE_CONECTIVIDAD, CommonsText.PROBLEMA_DE_CONECTIVIDAD, { timeOut: 5000 });
        });
    }

    private patchForm(value: ClientDataDTO) {
        if (!value) { return; }
        this.patching = true;
        this.contactForm.patchValue({ name: this.parseValue(value.name) });
        this.contactForm.patchValue({ surname: this.parseValue(value.surname) });
        this.contactForm.patchValue({ lastName: this.parseValue(value.lastName) });
        this.contactForm.patchValue({
            birthDate: value.birthDate ? this.parseValue(value.birthDate.replace(/\//g, '-')) : null
        });
        this.contactForm.patchValue({ email: this.parseValue(value.email) });
        this.contactForm.patchValue({ phone: this.parseValue(value.phone) });
        this.contactForm.patchValue({ phonePrefix: this.parsePairedPrefixValue(value.phone, value.phonePrefix) });
        this.contactForm.patchValue({ mobilePhone: this.parseValue(value.mobilePhone) });
        this.contactForm.patchValue({
            mobilePhonePrefix: this.parsePairedPrefixValue(value.mobilePhone, value.mobilePhonePrefix)
        });
        this.contactForm.patchValue({ address: this.parseValue(value.address) });
        this.contactForm.patchValue({ province: this.parseValue(value.province) });
        this.province = this.parseValue(value.province);
        this.contactForm.patchValue({ postalCode: this.parseValue(value.postalCode) });
        this.contactForm.patchValue({ cityCb: this.parseCityValue(value) });
        this.contactForm.patchValue({ roadType: this.parseSelectValue(value.roadType) });
        this.contactForm.patchValue({ number: this.parseValue(value.number) });
        this.contactForm.patchValue({ portal: this.parseValue(value.portal) });
        this.contactForm.patchValue({ stair: this.parseValue(value.stair) });
        this.contactForm.patchValue({ floor: this.parseValue(value.floor) });
        this.contactForm.patchValue({ door: this.parseValue(value.door) });
        this.contactForm.patchValue({ notificationChannel: this.parseValueRadioButton(value.notifications) });
        this.patching = false;
        this.documentType = value.documentType;
        this.documentNumber = value.documentNumber;
        this.contactProgress = this.clientService.getContactPercent(value);
        this.addressProgress = this.clientService.getAddressPercent(value);
    }

    onSave() {
        this.messages = [];
        if (this.running) { return; }
        // Se comprueba al enviar, no mientras se teclea: al rellenar el número el prefijo aún
        // está vacío y no tiene sentido avisar de ello hasta que se da por terminado el formulario.
        this.validatePhonePairs();
        if (this.contactForm.valid) {
            this.running = true;
            this.changeFormatBirthDate();
            this.clientService.onModifyContact(this.contactForm.value)
                .then(() => {
                    this.running = false;
                    this.toastr.success(CommonsText.PERFIL_ACTUALIZADO_EXITO, CommonsText.OPERACION_COMPLETADA, { timeOut: 5000 });
                    this.profileAction.emit({ section: 'profile' });
                })
                .catch(error => {
                    this.running = false;
                    this.handleSaveError(error);
                });
        } else {
            Object.keys(this.contactForm.controls).forEach(field => {
                this.contactForm.get(field).markAsTouched({ onlySelf: true });
            });
            this.contactForm.controls['cityCb'].markAsTouched({ onlySelf: true });
        }
    }

    onCancel() {
        this.profileAction.emit({ section: 'profile' });
    }

    private handleSaveError(error: any) {
        const devResponse = error.error && error.error.developerResponse;
        if (devResponse === this.CODE_ERROR_EMAIL_ALREADY_REGISTERED) {
            this.messages = ['Este correo electrónico ya está en uso.'];
            this.contactForm.get('email').setErrors({ incorrect: true });
            this.toastr.error(error.response, CommonsText.ERROR_OPERACION_CORREO_EN_USO, { timeOut: 5000 });
        } else {
            this.messages = (error.error && error.error.response) ? error.error.response : [CommonsText.PROBLEMA_DE_CONECTIVIDAD];
            if (error.error && error.error.response) {
                if (devResponse === this.ERROREMAILACONT) {
                    this.contactForm.get('email').setErrors({ incorrect: true });
                    this.contactForm.get('email').markAsTouched({ onlySelf: true });
                }
                if (devResponse === this.ERRORMOBILECONT) {
                    this.contactForm.get('mobilePhone').setErrors({ incorrect: true });
                    this.contactForm.get('mobilePhone').markAsTouched({ onlySelf: true });
                }
                if (devResponse === this.ERRORPHONECONT) {
                    this.contactForm.get('phone').setErrors({ incorrect: true });
                    this.contactForm.get('phone').markAsTouched({ onlySelf: true });
                }
                this.toastr.error(error.error.response, CommonsText.ERROR_DE_OPERACION, { timeOut: 5000 });
            }
        }
    }

    setMaxDate() {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 18);
        this.maxDate = date;
    }

    changeFormatBirthDate() {
        if (this.isCompany) {
            this.contactForm.value.birthDate = null;
        } else if (this.contactForm.value.birthDate) {
            if (this.contactForm.value.birthDate instanceof Date) {
                const d = this.contactForm.value.birthDate;
                this.contactForm.value.birthDate =
                    `${this.addLeadingZero(d.getDate())}-${this.addLeadingZero(d.getMonth() + 1)}-${d.getFullYear()}`;
            } else {
                // Formato con guiones (dd-mm-aaaa), como el resto de la app; el servicio
                // lo convierte a barras al enviarlo al backend.
                this.contactForm.value.birthDate = this.contactForm.value.birthDate.replace(/\//g, '-');
            }
        }
    }

    private addLeadingZero(value: number): string {
        return value < 10 ? `0${value}` : `${value}`;
    }

    checkContactPercent() {
        this.contactProgress = 100;
        ['name', 'surname', 'email', 'mobilePhone', 'birthDate'].forEach(ctrl => {
            if (this.contactForm.controls[ctrl].invalid) {
                this.contactProgress -= this.contactFactor;
            }
        });
    }

    checkAddressPercent() {
        this.addressProgress = 100;
        ['postalCode', 'cityCb', 'roadType', 'address', 'number'].forEach(ctrl => {
            if (this.contactForm.controls[ctrl].invalid) {
                this.addressProgress -= this.addressFactor;
            }
        });
    }

    searchCity() {
        const postalCode = this.contactForm.controls['postalCode'].value;
        if (postalCode && postalCode.length === 5 && !this.loadingCities) {
            this.loadingCities = true;
            this.clientService.onSearchCity(postalCode)
                .then(success => {
                    this.actualPostalCode = postalCode;
                    this.loadingCities = false;
                    this.cityList = [];
                    this.contactForm.controls['cityCb'].patchValue(null);
                    for (const city of success.response) {
                        this.cityList.push({ id: city.id, itemName: city.name, province: city.province });
                    }
                    if (success.response.length === 1) {
                        const city = success.response[0];
                        this.contactForm.patchValue({ cityCb: [{ id: city.id, itemName: city.name, province: city.province }] });
                        this.contactForm.patchValue({ province: this.parseValue(city.province) });
                        this.province = this.parseValue(city.province);
                    }
                })
                .catch(error => {
                    this.loadingCities = false;
                    this.toastr.error(error.response, CommonsText.ERROR_DE_OPERACION, { timeOut: 5000 });
                });
        }
    }

    onChangePostalCode(data: any) {
        const v = data.target.value;
        if (v.length === 5 && v !== this.actualPostalCode) {
            this.searchCity();
        }
    }

    onCitySelect(item: any) {
        this.province = this.parseValue(item.province);
        this.contactForm.patchValue({ province: this.parseValue(item.province) });
    }

    isInvalid(controlName: string): boolean {
        const control = this.contactForm.controls[controlName];
        return control.invalid && (control.touched || control.dirty);
    }

    hasError(controlName: string, errorKey: string): boolean {
        const control = this.contactForm.controls[controlName];
        return !!control && control.hasError(errorKey);
    }

    /**
     * Cada pareja teléfono/prefijo ha de informarse entera o dejarse entera en blanco. Marca en
     * el prefijo el error que corresponda, de forma que el formulario quede inválido y no se
     * llegue a enviar.
     */
    private validatePhonePairs(): void {
        this.PHONE_PAIRS.forEach(pair => {
            const phoneControl = this.contactForm.get(pair.phone);
            const prefixControl = this.contactForm.get(pair.prefix);

            const phoneValue = phoneControl.value;
            const hasPhone = (null !== phoneValue) && (undefined !== phoneValue)
                && ('' !== String(phoneValue).trim());
            // El prefijo es un multiselect: su valor es un array con el único elemento elegido.
            const prefixValue = prefixControl.value;
            const hasPrefix = !!prefixValue && (prefixValue.length > 0);

            this.setPairError(prefixControl, 'prefixRequired', hasPhone && !hasPrefix);
            this.setPairError(prefixControl, 'prefixNotAllowed', !hasPhone && hasPrefix);

            if (prefixControl.invalid) {
                prefixControl.markAsTouched({ onlySelf: true });
            }
        });
    }

    /**
     * Añade o quita una clave de error sin pisar las de los validadores del control, que
     * setErrors reemplaza por completo.
     */
    private setPairError(control: AbstractControl, errorKey: string, active: boolean): void {
        const errors = control.errors || {};
        if (active) {
            errors[errorKey] = true;
        } else {
            delete errors[errorKey];
        }
        control.setErrors(Object.keys(errors).length > 0 ? errors : null);
    }

    cambioPrefijo(controlName: string, event: any) {
        if (controlName === 'phone' && event.length > 0 && event[0].id !== this.prefijoFijoPhoneFlag) {
            const val = this.contactForm.get(controlName).value;
            this.contactForm.get(controlName).setErrors(null);
            this.prefijoFijoPhoneFlag = this.contactForm.get('phonePrefix').value[0].id;
            this.contactForm.get(controlName).setValue(null);
            this.contactForm.get(controlName).setValue(val);
        }
        if (controlName === 'mobilePhone' && event.length > 0 && event[0].id !== this.prefijoPhoneFlag) {
            const val = this.contactForm.get(controlName).value;
            this.contactForm.get(controlName).setErrors(null);
            this.prefijoPhoneFlag = this.contactForm.get('mobilePhonePrefix').value[0].id;
            this.contactForm.get(controlName).setValue(null);
            this.contactForm.get(controlName).setValue(val);
        }
    }

    getButtonLabel(): string {
        return this.running ? CommonsText.CARGANDO : CommonsText.GUARDAR_CAMBIOS_MINUSCULA;
    }

    private parseValue(value: any): any {
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
            return null;
        }
        return value;
    }

    private parseSelectValue(value: any): any {
        return (value !== null && value !== undefined) ? value : [];
    }

    /**
     * El backend siempre devuelve prefijo, incluso sin teléfono: CommonUtils.getPrefix() cae en
     * "34" por defecto. Cargarlo tal cual dejaría el formulario con un prefijo suelto y el aviso
     * de "prefijo sin teléfono" saltaría nada más guardar a quien no tenga fijo, así que el
     * prefijo solo se precarga si hay número al que acompañar.
     */
    private parsePairedPrefixValue(phone: any, prefix: any): any[] {
        const hasPhone = (null !== phone) && (undefined !== phone) && ('' !== String(phone).trim());
        return hasPhone ? this.parsePrefixValue(prefix) : null;
    }

    private parsePrefixValue(value: any): any[] {
        if (value === null || value === undefined) { return null; }
        let prefix: any;
        this.prefixData.forEach(n => { if (n.id === value) { prefix = n; } });
        return prefix !== undefined ? [prefix] : null;
    }

    private parseValueRadioButton(value: any): any {
        return value && value.length > 0 ? value[0] : null;
    }

    private parseCityValue(value: ClientDataDTO): any {
        if (!value.city || value.city.trim() === '') { return null; }
        this.cityList.push({ id: value.city, itemName: value.cityDescription, province: value.province });
        return [{ id: value.city, itemName: value.cityDescription }];
    }
}
